import { useState } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { ActorProfile, RateDetail, RateType } from '@mews-commander/data-access';
import {
    ContentSection,
    Dash,
    DividerList,
    LabeledListItem,
    LocalizedDateTimeFromUtc,
    LocalizedTextTag,
} from '@mews-commander/ui';
import { LocalizedEnum, lt } from '@mews/framework';
import { Avatar, Stack, Tag } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LanguageSwitcher } from '../forms/fields/LanguageSwitcher';

interface RateSidePanelContentProps {
    names: RateDetail['names'];
    shortNames: RateDetail['shortNames'];
    externalNames: RateDetail['externalNames'];
    descriptions: RateDetail['descriptions'];
    serviceName: string;
    baseRateName?: string | null;
    isDefault: boolean;
    type: RateType;
    rateGroupName: string;
    accountingCategoryName?: string | null;
    businessSegmentName?: string | null;
    defaultReservationPurpose?: string | null;
    pricingClassification?: string | null;
    rateKey?: string | null;
    ordering: number;
    creatorProfile: ActorProfile;
    createdUtc: string;
    updaterProfile: ActorProfile;
    updatedUtc: string;
}

export const RateSidePanelContent = ({
    names,
    shortNames,
    externalNames,
    descriptions,
    serviceName,
    baseRateName,
    isDefault,
    type,
    rateGroupName,
    accountingCategoryName,
    businessSegmentName,
    defaultReservationPurpose,
    pricingClassification,
    rateKey,
    ordering,
    creatorProfile,
    createdUtc,
    updaterProfile,
    updatedUtc,
}: RateSidePanelContentProps) => {
    const rateLanguages = Object.keys(names);
    const { defaultLanguageCode } = useCurrentEnterprise();
    const [languageCode, setLanguageCode] = useState(
        rateLanguages.includes(defaultLanguageCode) ? defaultLanguageCode : rateLanguages[0]
    );
    return (
        <Stack vertical spacing="400">
            <ContentSection title={lt('DetailPlural')}>
                <LanguageSwitcher
                    onLanguageChange={setLanguageCode}
                    rateLanguages={Object.keys(names)}
                    selectedLanguageCode={languageCode}
                />
                <DividerList>
                    <LabeledListItem label={lt('RateName')}>{names[languageCode] ?? <Dash />}</LabeledListItem>
                    <LabeledListItem label={lt('ShortName')}>{shortNames[languageCode] ?? <Dash />}</LabeledListItem>
                    <LabeledListItem label={lt('ExternalName')}>
                        {externalNames[languageCode] ?? <Dash />}
                    </LabeledListItem>
                    <LabeledListItem
                        label={lt('Description')}
                        itemAlignment="start"
                        contentOptions={{ whiteSpace: 'break-spaces' }}
                    >
                        {descriptions[languageCode] ?? <Dash />}
                    </LabeledListItem>
                    <LabeledListItem label={lt('Service')}>{serviceName}</LabeledListItem>
                    <LabeledListItem label={lt('BaseRate')}>{baseRateName ?? <Dash />}</LabeledListItem>
                    <LabeledListItem label={lt('Default')}>
                        {isDefault ? (
                            <LocalizedTextTag textKey={TextKey.Yes} variant="lime" />
                        ) : (
                            <LocalizedTextTag textKey={TextKey.No} variant="denim" />
                        )}
                    </LabeledListItem>
                    <LabeledListItem label={lt('RateVisibility')}>
                        <Tag variant={type === RateType.Public ? 'denim' : 'lime'}>
                            <LocalizedEnum prefix="RateType" value={type} />
                        </Tag>
                    </LabeledListItem>
                    <LabeledListItem label={lt('RateGroup')} itemAlignment="start">
                        {rateGroupName}
                    </LabeledListItem>
                    <LabeledListItem label={lt('AccountingCategory')}>
                        {accountingCategoryName ?? <Dash />}
                    </LabeledListItem>
                    <LabeledListItem label={lt('Segment')}>{businessSegmentName ?? <Dash />}</LabeledListItem>
                    <LabeledListItem label={lt('DefaultReservationPurpose')}>
                        {defaultReservationPurpose ?? <Dash />}
                    </LabeledListItem>
                    <LabeledListItem label={lt('PricingClassification')}>
                        {pricingClassification ?? <Dash />}
                    </LabeledListItem>
                    <LabeledListItem label={lt('RateKey')}>{rateKey ?? <Dash />}</LabeledListItem>
                    <LabeledListItem label={lt('Ordering')}>{ordering}</LabeledListItem>
                </DividerList>
            </ContentSection>
            <ContentSection title={lt('ActivityLog')}>
                <DividerList>
                    <LabeledListItem label={lt('CreatedBy')} itemAlignment="center">
                        <Stack itemAlignment="center" spacing="200">
                            <Avatar src={creatorProfile?.imageId ?? ''} size="medium" title={creatorProfile?.name} />
                            {creatorProfile?.name}
                        </Stack>
                    </LabeledListItem>
                    <LabeledListItem label={lt('CreatedOn')}>
                        <LocalizedDateTimeFromUtc utcString={createdUtc} />
                    </LabeledListItem>
                    <LabeledListItem label={lt('UpdatedBy')} itemAlignment="center">
                        <Stack itemAlignment="center" spacing="200">
                            <Avatar src={updaterProfile?.imageId ?? ''} size="medium" title={updaterProfile?.name} />
                            {updaterProfile?.name}
                        </Stack>
                    </LabeledListItem>
                    <LabeledListItem label={lt('UpdatedOn')}>
                        <LocalizedDateTimeFromUtc utcString={updatedUtc} />
                    </LabeledListItem>
                </DividerList>
            </ContentSection>
        </Stack>
    );
};
